import { GeoJSONSource, Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import { v4 } from 'uuid';
import { EventBus } from '../../../app/contexts/event-bus';
import assert from '../../../utils/assert';
import { DrawingObjectType } from '../types';
import { FeDrawMode } from './draw-mode';
import { FeDrawPolygonProperties } from './map-draw';

export class FePolygonMode extends FeDrawMode {
    private readonly CLICK_RADIUS_SQUARE = 25;
    constructor(
        private readonly map: MaplibreMap,
        private readonly source: GeoJSONSource
    ) {
        super();
    }

    private clickCount = 0;
    private feature: GeoJSON.Feature<GeoJSON.Polygon, FeDrawPolygonProperties> | null = null;

    override onClick(e: MapMouseEvent): void {
        e.originalEvent.stopPropagation();
        const point = e.lngLat.toArray();
        if (this.clickCount === 0) {
            this.feature = {
                type: 'Feature',
                id: v4(),
                geometry: {
                    type: 'Polygon',
                    coordinates: [[[...point], [...point]]]
                },
                properties: {
                    type: DrawingObjectType.polygon
                }
            };
            this.source.updateData({
                add: [this.feature]
            });
            this.clickCount++;
        } else if (this.clickCount < 2) {
            const geometry = this.feature!.geometry.coordinates[0];
            geometry.push(geometry[geometry.length - 1]);
            geometry.push(geometry[0]);
            this.clickCount++;
        } else {
            const mousePosition = e.point;
            const geometry = this.feature!.geometry.coordinates[0];
            const pointsCount = geometry.length - 2;
            // Find out if the user clicked on some point that is already part of the polygon but skip the last 2 points since they are the start point repeated and currently edit point
            if (
                geometry.some(
                    (x, i) => i !== pointsCount && this.map.project([x[0], x[1]]).distSqr(mousePosition) < this.CLICK_RADIUS_SQUARE
                )
            ) {
                // Remove the last point added
                geometry.splice(geometry.length - 2, 1);
                this.fireCreateFeature();
            } else {
                geometry.push([...geometry[geometry.length - 1]]);
                this.clickCount++;
            }
        }
    }

    override onMouseMove(e: MapMouseEvent): void {
        if (this.clickCount === 0) {
            return;
        }
        assert(this.feature);
        const geometry = this.feature.geometry.coordinates[0];
        const offset = this.clickCount < 2 ? 1 : 2;
        geometry[geometry.length - offset] = e.lngLat.toArray();
        this.updateFeature();
    }

    override cancel(): void {
        this.clickCount = 0;
        if (this.feature) {
            this.source.updateData({
                remove: [this.feature.id as string]
            });
        }
    }

    private fireCreateFeature() {
        if (this.feature && this.clickCount > 2) {
            this.source.updateData({ remove: [this.feature.id as string] });
            const feature = this.feature;
            this.feature = null;
            this.clickCount = 0;
            EventBus.fire('draw.create', {
                drawObject: {
                    type: DrawingObjectType.polygon,
                    points: feature.geometry.coordinates[0]
                }
            });
        }
    }

    private updateFeature() {
        this.source.updateData({
            update: [
                {
                    id: this.feature!.id as string,
                    newGeometry: this.feature!.geometry
                }
            ]
        });
    }

    override onKeyDown(e: KeyboardEvent): void {
        if (e.key !== ' ' && e.key !== 'Enter') {
            return;
        }
        e.preventDefault();

        if (this.clickCount <= 2) {
            return;
        }
        assert(this.feature);

        const coords = this.feature.geometry.coordinates[0];
        coords.splice(coords.length - 2, 1);

        this.fireCreateFeature();
    }
}
